import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { authAPI, plansAPI } from '../utils/api';
import { Save, Lock, LogOut, User, Mail, Phone, Building, Check, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function Profile() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const updateUser = useAuthStore((state) => state.updateUser);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const [profileData, setProfileData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    company_name: user?.company_name || '',
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await authAPI.getProfile();
        if (response.data && response.data.data && response.data.data.user) {
          setProfileData({
            first_name: response.data.data.user.first_name || '',
            last_name: response.data.data.user.last_name || '',
            email: response.data.data.user.email || '',
            phone: response.data.data.user.phone || '',
            company_name: response.data.data.user.company_name || '',
          });
        }
      } catch (err) {
        console.error('Failed to fetch profile info:', err);
      }
    };
    fetchProfile();
  }, []);

  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: '',
  });

  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchPlanInfo();
  }, [isAuthenticated, navigate]);

  const fetchPlanInfo = async () => {
    try {
      const response = await plansAPI.getCurrentPlan();
      setCurrentPlan(response.data.data);
    } catch (error) {
      console.error('Failed to fetch plan info:', error);
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setErrors({});
    setLoading(true);

    try {
      const updateData = {
        firstName: profileData.first_name,
        lastName: profileData.last_name,
        phone: profileData.phone,
        companyName: profileData.company_name,
      };
      await authAPI.updateProfile(updateData);
      updateUser({
        ...user,
        ...updateData,
      });
      setSuccessMessage('Profile updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrors({ profile: error.response?.data?.message || 'Failed to update profile' });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setErrors({});

    if (passwordData.new_password !== passwordData.confirm_password) {
      setErrors({ password: 'Passwords do not match' });
      return;
    }

    setLoading(true);
    try {
      await authAPI.changePassword({
        currentPassword: passwordData.old_password,
        newPassword: passwordData.new_password,
      });
      setPasswordData({ old_password: '', new_password: '', confirm_password: '' });
      setSuccessMessage('Password changed successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      const backendMsg = error.response?.data?.error || error.response?.data?.message;
      setErrors({ password: backendMsg || 'Failed to change password' });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-[#0B0E14] p-4 sm:p-6 md:p-8">
        {/* Header Section */}
        <div style={{direction: isRTL ? 'rtl' : 'ltr'}} className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-1 sm:mb-2">
            👤 {t('Profile Settings')}
          </h1>
          <p className="text-xs sm:text-base text-[#A0AEC0]">{t('Manage your account information and security')}</p>
        </div>

        {/* Success Alert */}
        {successMessage && (
          <div className={`mb-4 sm:mb-6 bg-[#128C7E]/20 border border-[#128C7E] text-[#128C7E] px-4 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl flex items-center gap-2 sm:gap-3 text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Check size={16} className="sm:w-4.5 sm:h-4.5 flex-shrink-0" />
            <span>{successMessage}</span>
          </div>
        )}

        <div className={`grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 ${isRTL ? 'text-right' : ''}`}>
          {/* Profile Information */}
          <div className="lg:col-span-2 bg-[#1A1D23]/40 backdrop-blur-lg border border-[#2D3748] rounded-lg sm:rounded-2xl p-4 sm:p-8">
            <h2 className={`text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <User size={20} className="sm:w-6 sm:h-6 text-[#128C7E]" />
              {t('Profile Information')}
            </h2>
            
            {errors.profile && (
              <div className={`mb-4 sm:mb-6 bg-red-500/20 border border-red-500 text-red-400 px-4 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl flex items-center gap-2 sm:gap-3 text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
                <AlertCircle size={16} className="sm:w-4.5 sm:h-4.5 flex-shrink-0" />
                <span>{errors.profile}</span>
              </div>
            )}

            <form onSubmit={handleSaveProfile} className="space-y-4 sm:space-y-6">
              {/* Name Row */}
              <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 ${isRTL ? 'text-right' : ''}`}>
                <div className="space-y-2">
                  <label className="text-[#A0AEC0] text-xs sm:text-sm font-semibold">{t('First Name')}</label>
                  <input
                    type="text"
                    name="first_name"
                    value={profileData.first_name}
                    onChange={handleProfileChange}
                    className="w-full bg-[#0B0E14] border border-[#2D3748] rounded-lg sm:rounded-xl px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base text-white placeholder-[#A0AEC0] focus:outline-none focus:border-[#128C7E] transition-colors"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[#A0AEC0] text-xs sm:text-sm font-semibold">{t('Last Name')}</label>
                  <input
                    type="text"
                    name="last_name"
                    value={profileData.last_name}
                    onChange={handleProfileChange}
                    className="w-full bg-[#0B0E14] border border-[#2D3748] rounded-lg sm:rounded-xl px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base text-white placeholder-[#A0AEC0] focus:outline-none focus:border-[#128C7E] transition-colors"
                  />
                </div>
              </div>

              {/* Email */}
              <div className="space-y-2">
                <label className={`text-[#A0AEC0] text-xs sm:text-sm font-semibold flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Mail size={14} className="sm:w-4 sm:h-4" />
                  {t('Email Address')}
                </label>
                <input
                  type="email"
                  name="email"
                  value={profileData.email}
                  disabled
                  className="w-full bg-[#0B0E14]/50 border border-[#2D3748] rounded-lg sm:rounded-xl px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base text-[#A0AEC0] cursor-not-allowed opacity-50"
                />
                <p className="text-xs text-[#A0AEC0]/70">{t('Email cannot be changed')}</p>
              </div>

              {/* Contact Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-2">
                  <label className={`text-[#A0AEC0] text-xs sm:text-sm font-semibold flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Phone size={14} className="sm:w-4 sm:h-4" />
                    {t('Phone')}
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={profileData.phone}
                    onChange={handleProfileChange}
                    placeholder={t('e.g. +962791234567')}
                    className="w-full bg-[#0B0E14] border border-[#2D3748] rounded-lg sm:rounded-xl px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base text-white placeholder-[#A0AEC0] focus:outline-none focus:border-[#128C7E] transition-colors"
                  />
                </div>
                <div className="space-y-2">
                  <label className={`text-[#A0AEC0] text-xs sm:text-sm font-semibold flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Building size={14} className="sm:w-4 sm:h-4" />
                    {t('Company')}
                  </label>
                  <input
                    type="text"
                    name="company_name"
                    value={profileData.company_name}
                    onChange={handleProfileChange}
                    placeholder={t('Your Company Name')}
                    className="w-full bg-[#0B0E14] border border-[#2D3748] rounded-lg sm:rounded-xl px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base text-white placeholder-[#A0AEC0] focus:outline-none focus:border-[#128C7E] transition-colors"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`w-full py-2 sm:py-3 bg-[#128C7E] text-white rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base hover:bg-[#0DA699] transition-all duration-300 hover:shadow-lg hover:shadow-[#128C7E]/20 flex items-center justify-center gap-2 disabled:opacity-50 ${isRTL ? 'flex-row-reverse' : ''}`}
              >
                <Save size={16} className="sm:w-4.5 sm:h-4.5" />
                {loading ? t('Saving...') : t('Save Changes')}
              </button>
            </form>
          </div>

          {/* Current Plan Widget */}
          {currentPlan && (
            <div className={`bg-[#1A1D23]/40 backdrop-blur-lg border border-[#2D3748] rounded-lg sm:rounded-2xl p-4 sm:p-8 h-fit ${isRTL ? 'text-right' : ''}`}>
              <h3 className={`text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>📊 {t('Current Plan')}</h3>
              <div className="space-y-3 sm:space-y-4">
                <div className="bg-[#0B0E14]/50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-[#128C7E]/20">
                  <p className="text-[#A0AEC0] text-xs sm:text-sm mb-1">{t('Plan Name')}</p>
                  <p className="text-xl sm:text-2xl font-bold text-[#128C7E]">{currentPlan.name}</p>
                </div>

                <div className="bg-[#0B0E14]/50 rounded-lg sm:rounded-xl p-3 sm:p-4">
                  <p className="text-[#A0AEC0] text-xs sm:text-sm mb-1">{t('Monthly Price')}</p>
                  <p className="text-xl sm:text-2xl font-bold text-white">
                    {currentPlan.price_usd ? `$${currentPlan.price_usd}` : t('Free')}
                  </p>
                </div>

                <div className="space-y-2">
                  <p className="text-[#A0AEC0] text-xs sm:text-sm font-semibold">✨ {t('Features')}</p>
                  <ul className="space-y-1 sm:space-y-2">
                    <li className="text-[#A0AEC0] text-xs sm:text-sm flex items-center gap-2">
                      <Check size={12} className="sm:w-3.5 sm:h-3.5 text-[#128C7E] flex-shrink-0" />
                      {currentPlan.otp_limit_monthly?.toLocaleString()} OTP/month
                    </li>
                    <li className="text-[#A0AEC0] text-xs sm:text-sm flex items-center gap-2">
                      <Check size={12} className="sm:w-3.5 sm:h-3.5 text-[#128C7E] flex-shrink-0" />
                      {currentPlan.api_keys_limit} {t('API Keys')}
                    </li>
                  </ul>
                </div>

                <button
                  onClick={() => navigate('/dashboard/billing')}
                  className="w-full py-2 sm:py-2.5 bg-[#128C7E]/20 border border-[#128C7E] text-[#128C7E] rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm hover:bg-[#128C7E]/30 transition-all duration-300"
                >
                  {t('View All Plans')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Change Password */}
        <div className="mt-4 sm:mt-6 bg-[#1A1D23]/40 backdrop-blur-lg border border-[#2D3748] rounded-lg sm:rounded-2xl p-4 sm:p-8">
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-2">
            <Lock size={20} className="sm:w-6 sm:h-6 text-[#128C7E]" />
            {t('Change Password')}
          </h2>

          {errors.password && (
            <div className="mb-4 sm:mb-6 bg-red-500/20 border border-red-500 text-red-400 px-4 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl flex items-center gap-2 sm:gap-3 text-sm">
              <AlertCircle size={16} className="sm:w-4.5 sm:h-4.5 flex-shrink-0" />
              <span>{errors.password}</span>
            </div>
          )}

          <form onSubmit={handleChangePassword} className="space-y-4 sm:space-y-6">
            <div className="space-y-2">
              <label className="text-[#A0AEC0] text-xs sm:text-sm font-semibold">{t('Current Password')}</label>
              <input
                type="password"
                name="old_password"
                value={passwordData.old_password}
                onChange={handlePasswordChange}
                className="w-full bg-[#0B0E14] border border-[#2D3748] rounded-lg sm:rounded-xl px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base text-white placeholder-[#A0AEC0] focus:outline-none focus:border-[#128C7E] transition-colors"
                required
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-2">
                <label className="text-[#A0AEC0] text-xs sm:text-sm font-semibold">{t('New Password')}</label>
                <input
                  type="password"
                  name="new_password"
                  value={passwordData.new_password}
                  onChange={handlePasswordChange}
                  className="w-full bg-[#0B0E14] border border-[#2D3748] rounded-xl px-4 py-3 text-white placeholder-[#A0AEC0] focus:outline-none focus:border-[#128C7E] transition-colors"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-[#A0AEC0] text-sm font-semibold">{t('Confirm Password')}</label>
                <input
                  type="password"
                  name="confirm_password"
                  value={passwordData.confirm_password}
                  onChange={handlePasswordChange}
                  className="w-full bg-[#0B0E14] border border-[#2D3748] rounded-xl px-4 py-3 text-white placeholder-[#A0AEC0] focus:outline-none focus:border-[#128C7E] transition-colors"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-[#128C7E] text-white rounded-xl font-semibold hover:bg-[#0DA699] transition-all duration-300 hover:shadow-lg hover:shadow-[#128C7E]/20 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Lock size={18} />
              {loading ? t('Updating...') : t('Update Password')}
            </button>
          </form>
        </div>

        {/* Logout Section */}
        <div className="mt-4 sm:mt-6 bg-red-500/10 backdrop-blur-lg border border-red-500/30 rounded-lg sm:rounded-2xl p-4 sm:p-8">
          <h2 className="text-lg sm:text-2xl font-bold text-white mb-2 sm:mb-3 flex items-center gap-2">
            <LogOut size={20} className="sm:w-6 sm:h-6 text-red-500" />
            {t('Sign Out')}
          </h2>
          <p className="text-xs sm:text-base text-[#A0AEC0] mb-4 sm:mb-6">{t('Sign out from all devices')}</p>
          <button
            onClick={handleLogout}
            className="px-4 sm:px-6 py-2 sm:py-3 bg-red-500 text-white rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base hover:bg-red-600 transition-all duration-300 flex items-center gap-2"
          >
            <LogOut size={16} className="sm:w-4.5 sm:h-4.5" />
            {t('Sign Out')}
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
}
